import { useEffect, useState } from 'react';
import { coreAPI } from '../lib/api';

/**
 * CR-4141: whether the signed-in user's role may approve or reject suppliers.
 *
 * Mirrors the backend gate exactly — allowed only when the user's role has
 * `can_approve_orders` or `is_admin_role`. A user with no role is refused
 * (CR-7077), as is a role with neither (e.g. Buyer).
 *
 * Defaults to `true` so legitimate approvers never see a flash of hidden
 * controls while the profile request is in flight, and stays `true` if the
 * request fails — the backend still returns 403, so the UI never grants an
 * action the API would refuse.
 *
 * @param enabled - Skip the lookup when false (e.g. supplier portal mode).
 * @returns True when the user may approve/reject suppliers.
 */
export function useCanApproveSuppliers(enabled: boolean = true): boolean {
  const [canApprove, setCanApprove] = useState(true);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    coreAPI.getMyProfile()
      .then(res => {
        if (cancelled) return;
        const role = res.data.role_details;
        setCanApprove(Boolean(role && (role.can_approve_orders || role.is_admin_role)));
      })
      .catch(() => { /* leave default; backend still enforces the 403 */ });
    return () => { cancelled = true; };
  }, [enabled]);

  return canApprove;
}
